import { RoleGate } from "@/components/auth/RoleGate";
import PageHeader from "@/components/common/PageHeader";
import StatusSelect from "@/pages/tenants/components/StatusSelect";
import { governanceService } from "@/services/governance.service";
import { impersonationService } from "@/services/impersonation.service";
import { moduleService } from "@/services/module.service";
import {
  monitoringService,
  type DailyMetrics,
  type ErrorGroup,
  type ErrorSummary,
  type OnlineConnection,
  type StorageUsage,
  type TenantHealthDetail,
} from "@/services/monitoring.service";
import { subscriptionService } from "@/services/subscription.service";
import { tenantService } from "@/services/tenant.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useParams, useSearchParams } from "react-router-dom";

// ── Tab definitions ────────────────────────────────────────

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "health", label: "Health & Metrics" },
  { key: "storage", label: "Storage" },
  { key: "errors", label: "Errors" },
  { key: "presence", label: "Presence & Sync" },
  { key: "usage", label: "Usage" },
  { key: "sessions", label: "Sessions" },
  { key: "alerts", label: "Alerts" },
  { key: "lifecycle", label: "Lifecycle" },
  { key: "config", label: "Configuration" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

// ── Helpers ────────────────────────────────────────────────

function riskColor(level: string) {
  switch (level) {
    case "critical":
      return "text-red-600 bg-red-50";
    case "at_risk":
      return "text-orange-600 bg-orange-50";
    case "monitor":
      return "text-yellow-600 bg-yellow-50";
    case "healthy":
      return "text-green-600 bg-green-50";
    default:
      return "text-slate-500 bg-slate-50";
  }
}

function scoreBarColor(score: number) {
  if (score >= 80) return "bg-green-500";
  if (score >= 60) return "bg-yellow-500";
  if (score >= 40) return "bg-orange-500";
  return "bg-red-500";
}

function formatMB(mb: number) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
      {children}
    </h3>
  );
}

function ScoreBar({ label, score }: { label: string; score: number }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-900">{score}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100">
        <div
          className={`h-2 rounded-full transition-all ${scoreBarColor(score)}`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────

function TenantDetailPage() {
  const params = useParams();
  const tenantId = params.id ?? "";
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as TabKey) || "overview";

  const queryClient = useQueryClient();

  // ── Core tenant data ───────────────────────────────────

  const { data, isLoading } = useQuery({
    queryKey: ["admin-tenant", tenantId],
    queryFn: () => tenantService.getById(tenantId),
    enabled: Boolean(tenantId),
  });

  // ── Monitoring data (fetched per tab) ──────────────────

  const { data: healthData } = useQuery({
    queryKey: ["tenant-health", tenantId],
    queryFn: () => monitoringService.getTenantHealth(tenantId),
    enabled: Boolean(tenantId),
    retry: false,
  });

  const { data: healthHistory } = useQuery({
    queryKey: ["tenant-health-history", tenantId],
    queryFn: () => monitoringService.getTenantHealthHistory(tenantId, 30),
    enabled:
      Boolean(tenantId) && (activeTab === "health" || activeTab === "overview"),
    retry: false,
  });

  const { data: storageData } = useQuery({
    queryKey: ["tenant-storage", tenantId],
    queryFn: () => monitoringService.getTenantStorage(tenantId),
    enabled:
      Boolean(tenantId) &&
      (activeTab === "storage" || activeTab === "overview"),
    retry: false,
  });

  const { data: latestMetrics } = useQuery({
    queryKey: ["tenant-metrics-latest", tenantId],
    queryFn: () => monitoringService.getTenantMetricsLatest(tenantId),
    enabled: Boolean(tenantId),
    retry: false,
  });

  const { data: metricsHistory } = useQuery({
    queryKey: ["tenant-metrics-history", tenantId],
    queryFn: () => monitoringService.getTenantMetrics(tenantId),
    enabled: Boolean(tenantId) && activeTab === "health",
    retry: false,
  });

  const { data: errorSummary } = useQuery({
    queryKey: ["tenant-error-summary", tenantId],
    queryFn: () => monitoringService.getTenantErrorSummary(tenantId),
    enabled:
      Boolean(tenantId) && (activeTab === "errors" || activeTab === "overview"),
    retry: false,
  });

  const { data: errorGroups } = useQuery({
    queryKey: ["tenant-errors", tenantId],
    queryFn: () =>
      monitoringService.getTenantErrors(tenantId, {
        limit: 10,
        sortBy: "lastSeenAt",
        sortOrder: "desc",
      }),
    enabled: Boolean(tenantId) && activeTab === "errors",
    retry: false,
  });

  const { data: onlineUsers } = useQuery({
    queryKey: ["tenant-presence", tenantId],
    queryFn: () => monitoringService.getOnlineConnections({ tenantId }),
    enabled:
      Boolean(tenantId) &&
      (activeTab === "presence" || activeTab === "overview"),
    retry: false,
  });

  const [status, setStatus] = useState<string>("");
  const [partnerCode, setPartnerCode] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [impersonationReason, setImpersonationReason] = useState<string>("");
  const [capabilityReason, setCapabilityReason] = useState<string>("");
  const [pageOverrideReason, setPageOverrideReason] = useState<string>("");
  const [tenantChangeReason, setTenantChangeReason] = useState<string>("");
  const [impersonationLaunchUrl, setImpersonationLaunchUrl] = useState<string | null>(
    null,
  );
  const [impersonationLogId, setImpersonationLogId] = useState<string | null>(
    null,
  );

  // Store editing state - MOVED BEFORE EARLY RETURNS
  const [editingStoreId, setEditingStoreId] = useState<string | null>(null);
  const [storeDraft, setStoreDraft] = useState({
    name: "",
    city: "",
    state: "",
    status: "",
  });

  // Plan/subscription state - MOVED BEFORE EARLY RETURNS
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const [useTrial, setUseTrial] = useState(false);
  const [subscriptionAssignmentReason, setSubscriptionAssignmentReason] =
    useState("");

  useEffect(() => {
    if (data) {
      setStatus(data.status);
      setPartnerCode(data.partnerCode || "");
      if (!selectedUserId && data.users.length) {
        setSelectedUserId(data.users[0].id);
      }
    }
  }, [data, selectedUserId]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { status?: string; partnerCode?: string };
    }) => tenantService.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenant", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
      toast.success("Tenant updated");
    },
  });

  const { mutateAsync: updateStore, isPending: isUpdatingStore } = useMutation({
    mutationFn: ({
      storeId,
      payload,
    }: {
      storeId: string;
      payload: {
        name?: string;
        city?: string;
        state?: string;
        status?: string;
      };
    }) => tenantService.updateStore(tenantId, storeId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenant", tenantId] });
      toast.success("Store updated");
      setEditingStoreId(null);
    },
  });

  const {
    mutateAsync: updatePaymentMethod,
    isPending: isUpdatingPaymentMethod,
  } = useMutation({
    mutationFn: ({
      storeId,
      method,
      enabled,
    }: {
      storeId: string;
      method: string;
      enabled: boolean;
    }) =>
      tenantService.updatePaymentMethod(tenantId, storeId, method, {
        isEnabled: enabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenant", tenantId] });
      toast.success("Payment method updated");
    },
  });

  const {
    mutateAsync: updateBillingPrinter,
    isPending: isUpdatingBillingPrinter,
  } = useMutation({
    mutationFn: ({ storeId, active }: { storeId: string; active: boolean }) =>
      tenantService.updateBillingPrinter(tenantId, storeId, {
        isActive: active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenant", tenantId] });
      toast.success("Billing printer placeholder updated");
    },
  });

  const { data: plans } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => subscriptionService.listPlans(),
  });

  useEffect(() => {
    if (plans?.length && !selectedPlanId) {
      setSelectedPlanId(plans[0].id);
    }
  }, [plans, selectedPlanId]);

  const selectedPlan = plans?.find((plan) => plan.id === selectedPlanId);

  useEffect(() => {
    if (!selectedPlan?.trialDays && useTrial) {
      setUseTrial(false);
    }
  }, [selectedPlan?.trialDays, useTrial]);

  const { mutateAsync: createSubscription, isPending: isCreatingSubscription } =
    useMutation({
      mutationFn: () =>
        tenantService.createSubscription(tenantId, {
          planId: selectedPlanId,
          interval: billingInterval,
          status: useTrial && selectedPlan?.trialDays ? "TRIAL" : "ACTIVE",
          reason: subscriptionAssignmentReason.trim(),
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["admin-tenant", tenantId] });
        toast.success("Subscription created");
        setSubscriptionAssignmentReason("");
      },
    });

  const {
    mutateAsync: startImpersonation,
    isPending: isStartingImpersonation,
  } = useMutation({
    mutationFn: () =>
      impersonationService.start({
        tenantId,
        userId: selectedUserId,
        reason: impersonationReason.trim(),
      }),
    onSuccess: (response) => {
      const tenantAppUrl = (
        import.meta.env.VITE_TENANT_APP_URL || "http://localhost:5173"
      ).replace(/\/$/, "");
      const fragment = new URLSearchParams({
        exchangeCode: response.exchangeCode,
      }).toString();
      setImpersonationLaunchUrl(`${tenantAppUrl}/impersonation#${fragment}`);
      setImpersonationLogId(response.impersonationLogId);
      toast.success("One-time tenant session is ready for 60 seconds");
    },
  });

  const { mutateAsync: stopImpersonation, isPending: isStoppingImpersonation } =
    useMutation({
      mutationFn: () => impersonationService.stop(impersonationLogId ?? ""),
      onSuccess: () => {
        toast.success("Impersonation session ended");
        setImpersonationLogId(null);
        setImpersonationLaunchUrl(null);
      },
    });

  const { mutateAsync: forceLogout, isPending: isForceLoggingOut } =
    useMutation({
      mutationFn: () => tenantService.forceLogout(tenantId),
      onSuccess: (result) => {
        toast.success(result.message);
      },
    });

  const { mutateAsync: syncPermissions, isPending: isSyncingPermissions } =
    useMutation({
      mutationFn: () => governanceService.syncTenantPermissions(tenantId),
      onSuccess: (result) => {
        toast.success(result.message);
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.message || "Failed to sync permissions",
        );
      },
    });

  const { data: tenantModules, isLoading: isLoadingModules } = useQuery({
    queryKey: ["admin-tenant-modules", tenantId],
    queryFn: () => moduleService.getTenantModules(tenantId),
    enabled: Boolean(tenantId),
  });

  const { data: allModules } = useQuery({
    queryKey: ["admin-modules"],
    queryFn: () => moduleService.getAllModules(),
  });

  const {
    data: pagePolicies,
    isLoading: isLoadingPagePolicies,
    isError: isPagePoliciesError,
  } = useQuery({
    queryKey: ["admin-page-policies"],
    queryFn: () => moduleService.getPagePolicies(),
  });

  const {
    data: tenantPageOverrides,
    isLoading: isLoadingPageOverrides,
    isError: isPageOverridesError,
  } = useQuery({
    queryKey: ["admin-tenant-page-overrides", tenantId],
    queryFn: () => moduleService.getTenantPageOverrides(tenantId),
    enabled: Boolean(tenantId),
  });

  const { mutateAsync: toggleModule, isPending: isTogglingModule } =
    useMutation({
      mutationFn: async ({
        code,
        mode,
      }: {
        code: string;
        mode: "DEFAULT" | "ENABLED" | "DISABLED";
      }) => {
        if (mode === "DEFAULT") {
          return moduleService.removeModuleOverride(
            tenantId,
            code,
            capabilityReason.trim(),
          );
        }
        if (mode === "ENABLED") {
          return moduleService.enableModuleForTenant(tenantId, {
            moduleCode: code,
            reason: capabilityReason.trim(),
          });
        }
        return moduleService.disableModuleForTenant(tenantId, {
          moduleCode: code,
          reason: capabilityReason.trim(),
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["admin-tenant-modules", tenantId],
        });
        toast.success("Module updated");
        setCapabilityReason("");
      },
      onError: () => toast.error("Capability override was not applied"),
    });

  const { mutateAsync: setPageMode, isPending: isSettingPageMode } =
    useMutation({
      mutationFn: ({
        pageKey,
        mode,
      }: {
        pageKey: string;
        mode: "DEFAULT" | "HIDDEN" | "DENIED";
      }) =>
        mode === "DEFAULT"
          ? moduleService.removeTenantPageOverride(
              tenantId,
              pageKey,
              pageOverrideReason.trim(),
            )
          : moduleService.setTenantPageOverride(tenantId, pageKey, {
              mode,
              reason: pageOverrideReason.trim(),
            }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["admin-tenant-page-overrides", tenantId],
        });
        queryClient.invalidateQueries({
          queryKey: ["admin-tenant-modules", tenantId],
        });
        toast.success("Page access updated");
        setPageOverrideReason("");
      },
      onError: (error: any) =>
        toast.error(
          error?.response?.data?.message || "Page access was not updated",
        ),
    });

  const summary = useMemo(() => {
    if (!data) return [];
    return [
      { label: "Users", value: data._count?.users ?? 0 },
      { label: "Products", value: data._count?.products ?? 0 },
      { label: "Invoices", value: data._count?.invoices ?? 0 },
    ];
  }, [data]);

  // EARLY RETURNS - After all hooks
  if (isLoading) {
    return <div className="text-sm text-slate-500">Loading tenant...</div>;
  }

  if (!data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-500">Tenant not found.</p>
        <Link className="text-sm font-semibold text-slate-900" to="/tenants">
          Back to tenants
        </Link>
      </div>
    );
  }

  const statusValue = status || data.status;

  function setTab(tab: TabKey) {
    setSearchParams({ tab });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.name}
        subtitle={`${data.email} · ${data.status}`}
        actions={
          <div className="flex gap-2">
            <Link
              to={`/tenants/${tenantId}/control-plane`}
              className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-slate-700"
            >
              Open control plane
            </Link>
            <Link
              to="/tenants"
              className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
            >
              Back to tenants
            </Link>
          </div>
        }
      />

      {/* Quick summary strip */}
      <div className="flex flex-wrap items-center gap-3">
        {healthData && (
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${riskColor(healthData.riskLevel)}`}
          >
            Health: {healthData.healthScore}/100 (
            {healthData.riskLevel.replace("_", " ")})
          </span>
        )}
        {onlineUsers && (
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            {onlineUsers.length} online now
          </span>
        )}
        {errorSummary && errorSummary.open > 0 && (
          <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
            {errorSummary.open} open errors
          </span>
        )}
        {storageData && (
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              storageData.utilizationPercent > 80
                ? "bg-red-50 text-red-600"
                : storageData.utilizationPercent > 60
                  ? "bg-yellow-50 text-yellow-600"
                  : "bg-green-50 text-green-600"
            }`}
          >
            Storage: {storageData.utilizationPercent.toFixed(0)}%
          </span>
        )}
      </div>

      {/* Tab bar */}
      <nav className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setTab(tab.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition ${
              activeTab === tab.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      {activeTab === "overview" && (
        <OverviewTab
          data={data}
          summary={summary}
          healthData={healthData}
          latestMetrics={latestMetrics}
          storageData={storageData}
          errorSummary={errorSummary}
          onlineUsers={onlineUsers}
        />
      )}
      {activeTab === "health" && (
        <HealthTab
          healthData={healthData}
          healthHistory={healthHistory}
          latestMetrics={latestMetrics}
          metricsHistory={metricsHistory}
        />
      )}
      {activeTab === "storage" && <StorageTab storageData={storageData} />}
      {activeTab === "errors" && (
        <ErrorsTab errorSummary={errorSummary} errorGroups={errorGroups} />
      )}
      {activeTab === "presence" && <PresenceTab onlineUsers={onlineUsers} />}
      {activeTab === "usage" && <UsageTab tenantId={tenantId} />}
      {activeTab === "sessions" && <SessionsTab tenantId={tenantId} />}
      {activeTab === "alerts" && <AlertsTab tenantId={tenantId} />}
      {activeTab === "lifecycle" && <LifecycleTab tenantId={tenantId} />}
      {activeTab === "config" && (
        <ConfigTab
          data={data}
          statusValue={statusValue}
          setStatus={setStatus}
          partnerCode={partnerCode}
          setPartnerCode={setPartnerCode}
          tenantChangeReason={tenantChangeReason}
          setTenantChangeReason={setTenantChangeReason}
          isPending={isPending}
          mutateAsync={mutateAsync}
          tenantId={tenantId}
          isForceLoggingOut={isForceLoggingOut}
          forceLogout={forceLogout}
          isSyncingPermissions={isSyncingPermissions}
          syncPermissions={syncPermissions}
          plans={plans}
          selectedPlanId={selectedPlanId}
          setSelectedPlanId={setSelectedPlanId}
          billingInterval={billingInterval}
          setBillingInterval={setBillingInterval}
          useTrial={useTrial}
          setUseTrial={setUseTrial}
          selectedPlan={selectedPlan}
          subscriptionAssignmentReason={subscriptionAssignmentReason}
          setSubscriptionAssignmentReason={setSubscriptionAssignmentReason}
          isCreatingSubscription={isCreatingSubscription}
          createSubscription={createSubscription}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          impersonationReason={impersonationReason}
          setImpersonationReason={setImpersonationReason}
          capabilityReason={capabilityReason}
          setCapabilityReason={setCapabilityReason}
          isStartingImpersonation={isStartingImpersonation}
          startImpersonation={startImpersonation}
          impersonationLaunchUrl={impersonationLaunchUrl}
          impersonationLogId={impersonationLogId}
          isStoppingImpersonation={isStoppingImpersonation}
          stopImpersonation={stopImpersonation}
          allModules={allModules}
          tenantModules={tenantModules}
          isLoadingModules={isLoadingModules}
          isTogglingModule={isTogglingModule}
          toggleModule={toggleModule}
          pagePolicies={pagePolicies}
          tenantPageOverrides={tenantPageOverrides}
          isLoadingPagePolicies={isLoadingPagePolicies}
          isLoadingPageOverrides={isLoadingPageOverrides}
          isPagePoliciesError={isPagePoliciesError}
          isPageOverridesError={isPageOverridesError}
          pageOverrideReason={pageOverrideReason}
          setPageOverrideReason={setPageOverrideReason}
          isSettingPageMode={isSettingPageMode}
          setPageMode={setPageMode}
          editingStoreId={editingStoreId}
          setEditingStoreId={setEditingStoreId}
          storeDraft={storeDraft}
          setStoreDraft={setStoreDraft}
          isUpdatingStore={isUpdatingStore}
          updateStore={updateStore}
          isUpdatingPaymentMethod={isUpdatingPaymentMethod}
          updatePaymentMethod={updatePaymentMethod}
          isUpdatingBillingPrinter={isUpdatingBillingPrinter}
          updateBillingPrinter={updateBillingPrinter}
        />
      )}
    </div>
  );
}

// ── Overview Tab ───────────────────────────────────────────

function OverviewTab({
  data,
  summary,
  healthData,
  latestMetrics,
  storageData,
  errorSummary,
  onlineUsers,
}: {
  data: any;
  summary: Array<{ label: string; value: number }>;
  healthData?: TenantHealthDetail;
  latestMetrics?: DailyMetrics | null;
  storageData?: StorageUsage;
  errorSummary?: ErrorSummary;
  onlineUsers?: OnlineConnection[];
}) {
  return (
    <div className="space-y-6">
      <section className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {summary.map((item) => (
          <Card key={item.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              {item.label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {item.value}
            </p>
          </Card>
        ))}
        {latestMetrics && (
          <>
            <Card>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Active Users
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {latestMetrics.activeUsers}
              </p>
              <p className="text-xs text-slate-400">
                of {latestMetrics.totalUsers} total
              </p>
            </Card>
            <Card>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Today&apos;s Logins
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {latestMetrics.totalLogins}
              </p>
              <p className="text-xs text-slate-400">
                {latestMetrics.uniqueLogins} unique
              </p>
            </Card>
            <Card>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Error Rate
              </p>
              <p
                className={`mt-2 text-2xl font-semibold ${latestMetrics.errorRate > 5 ? "text-red-600" : "text-slate-900"}`}
              >
                {latestMetrics.errorRate.toFixed(1)}%
              </p>
            </Card>
          </>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <SectionTitle>Profile</SectionTitle>
          <div className="mt-4 space-y-2 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-900">City:</span>{" "}
              {data.city || "—"}
            </p>
            <p>
              <span className="font-semibold text-slate-900">State:</span>{" "}
              {data.state || "—"}
            </p>
            <p>
              <span className="font-semibold text-slate-900">GST:</span>{" "}
              {data.gstNumber || "—"}
            </p>
            <p>
              <span className="font-semibold text-slate-900">Partner:</span>{" "}
              {data.partnerCode || "—"}
            </p>
            <p>
              <span className="font-semibold text-slate-900">Created:</span>{" "}
              {new Date(data.createdAt).toLocaleDateString()}
            </p>
          </div>
        </Card>

        {healthData ? (
          <Card>
            <SectionTitle>Health Score</SectionTitle>
            <div className="mt-4 flex items-start gap-6">
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold ${riskColor(healthData.riskLevel)}`}
                >
                  {healthData.healthScore}
                </div>
                <span className="mt-2 text-xs font-semibold capitalize text-slate-500">
                  {healthData.riskLevel.replace("_", " ")}
                </span>
                {healthData.scoreDelta !== null && (
                  <span
                    className={`mt-1 text-xs font-semibold ${
                      healthData.scoreDelta > 0
                        ? "text-green-600"
                        : healthData.scoreDelta < 0
                          ? "text-red-600"
                          : "text-slate-400"
                    }`}
                  >
                    {healthData.scoreDelta > 0 ? "+" : ""}
                    {healthData.scoreDelta} from last
                  </span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <ScoreBar
                  label="Login Activity"
                  score={healthData.loginScore}
                />
                <ScoreBar
                  label="Feature Adoption"
                  score={healthData.featureScore}
                />
                <ScoreBar
                  label="Transactions"
                  score={healthData.transactionScore}
                />
                <ScoreBar label="Error Rate" score={healthData.errorScore} />
                <ScoreBar label="Sync Health" score={healthData.syncScore} />
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <SectionTitle>Health Score</SectionTitle>
            <p className="mt-4 text-sm text-slate-400">
              No health data available yet.
            </p>
          </Card>
        )}

        {storageData && (
          <Card>
            <SectionTitle>Storage Usage</SectionTitle>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {formatMB(storageData.totalStorageMB)} /{" "}
                  {formatMB(storageData.quotaLimitMB)}
                </span>
                <span className="font-semibold text-slate-900">
                  {storageData.utilizationPercent.toFixed(1)}%
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-slate-100">
                <div
                  className={`h-3 rounded-full transition-all ${
                    storageData.utilizationPercent > 90
                      ? "bg-red-500"
                      : storageData.utilizationPercent > 70
                        ? "bg-yellow-500"
                        : "bg-green-500"
                  }`}
                  style={{
                    width: `${Math.min(storageData.utilizationPercent, 100)}%`,
                  }}
                />
              </div>
              {storageData.daysUntilFull && (
                <p className="text-xs text-slate-400">
                  ~{storageData.daysUntilFull} days until full at current growth
                  rate
                </p>
              )}
            </div>
          </Card>
        )}

        {errorSummary && (
          <Card>
            <SectionTitle>Error Summary</SectionTitle>
            <div className="mt-4 grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-semibold text-red-600">
                  {errorSummary.open}
                </p>
                <p className="text-xs text-slate-400">Open</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-orange-600">
                  {errorSummary.regressed}
                </p>
                <p className="text-xs text-slate-400">Regressed</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-green-600">
                  {errorSummary.resolved}
                </p>
                <p className="text-xs text-slate-400">Resolved</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate-900">
                  {errorSummary.today}
                </p>
                <p className="text-xs text-slate-400">Today</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {onlineUsers && onlineUsers.length > 0 && (
        <Card>
          <SectionTitle>Online Users ({onlineUsers.length})</SectionTitle>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="pb-2 pr-4">User</th>
                  <th className="pb-2 pr-4">Platform</th>
                  <th className="pb-2 pr-4">Page</th>
                  <th className="pb-2 pr-4">Last Heartbeat</th>
                </tr>
              </thead>
              <tbody>
                {onlineUsers.slice(0, 5).map((conn) => (
                  <tr key={conn.deviceId} className="border-b border-slate-50">
                    <td className="py-2 pr-4 text-slate-700">
                      {conn.userId.slice(0, 8)}...
                    </td>
                    <td className="py-2 pr-4">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                        {conn.platform}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-slate-500">
                      {conn.appState?.currentPage || "—"}
                    </td>
                    <td className="py-2 pr-4 text-xs text-slate-400">
                      {new Date(conn.lastHeartbeat).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Health & Metrics Tab ───────────────────────────────────

function HealthTab({
  healthData,
  healthHistory,
  latestMetrics,
  metricsHistory,
}: {
  healthData?: TenantHealthDetail;
  healthHistory?: TenantHealthDetail[];
  latestMetrics?: DailyMetrics | null;
  metricsHistory?: DailyMetrics[];
}) {
  return (
    <div className="space-y-6">
      {healthData ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <SectionTitle>Health Score Breakdown</SectionTitle>
            <div className="mt-4 flex items-center gap-6">
              <div
                className={`flex h-24 w-24 shrink-0 items-center justify-center rounded-full text-3xl font-bold ${riskColor(healthData.riskLevel)}`}
              >
                {healthData.healthScore}
              </div>
              <div className="flex-1 space-y-2">
                <ScoreBar
                  label="Login Activity (20%)"
                  score={healthData.loginScore}
                />
                <ScoreBar
                  label="Feature Adoption (15%)"
                  score={healthData.featureScore}
                />
                <ScoreBar
                  label="Transactions (15%)"
                  score={healthData.transactionScore}
                />
                <ScoreBar
                  label="Error Rate (15%)"
                  score={healthData.errorScore}
                />
                <ScoreBar
                  label="Sync Health (10%)"
                  score={healthData.syncScore}
                />
                <ScoreBar
                  label="Billing (10%)"
                  score={healthData.billingScore}
                />
                <ScoreBar
                  label="Storage (5%)"
                  score={healthData.storageScore}
                />
                <ScoreBar
                  label="Support (5%)"
                  score={healthData.supportScore}
                />
                <ScoreBar label="Uptime (5%)" score={healthData.uptimeScore} />
              </div>
            </div>
          </Card>

          <Card>
            <SectionTitle>Recommendations</SectionTitle>
            <div className="mt-4 space-y-2">
              {(healthData.recommendations ?? []).length > 0 ? (
                (healthData.recommendations as string[]).map((rec, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
                  >
                    <span className="mt-0.5 text-amber-500">&#9888;</span>
                    <span>{rec}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-green-600">
                  All health factors look good!
                </p>
              )}
            </div>
          </Card>
        </div>
      ) : (
        <Card>
          <SectionTitle>Health Score</SectionTitle>
          <p className="mt-4 text-sm text-slate-400">
            No health score computed yet.
          </p>
        </Card>
      )}

      {healthHistory && healthHistory.length > 0 && (
        <Card>
          <SectionTitle>Health Score Trend (30 days)</SectionTitle>
          <div className="mt-4 overflow-x-auto">
            <div
              className="flex items-end gap-1"
              style={{ minWidth: healthHistory.length * 24 }}
            >
              {healthHistory.map((h, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center"
                  style={{ width: 20 }}
                >
                  <span className="text-[10px] text-slate-400">
                    {h.healthScore}
                  </span>
                  <div
                    className={`w-3 rounded-t ${scoreBarColor(h.healthScore)}`}
                    style={{
                      height: `${Math.max(h.healthScore * 0.8, 4)}px`,
                    }}
                    title={`${new Date(h.computedAt).toLocaleDateString()}: ${h.healthScore}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {latestMetrics && (
        <Card>
          <SectionTitle>Latest Daily Metrics</SectionTitle>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs text-slate-400">Active / Total Users</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.activeUsers} / {latestMetrics.totalUsers}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Logins (Unique)</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.totalLogins} ({latestMetrics.uniqueLogins})
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Invoices</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.totalInvoices}
              </p>
              <p className="text-xs text-slate-400">
                &#8377;{(latestMetrics.invoiceAmount ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Products (New)</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.totalProducts} (+{latestMetrics.newProducts})
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Customers (New)</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.totalCustomers} (+{latestMetrics.newCustomers})
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Purchase Orders</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.purchaseOrders}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Sync Events / Errors</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.syncEvents} / {latestMetrics.syncErrors}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Telemetry Events</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMetrics.telemetryEvents}
              </p>
            </div>
          </div>
        </Card>
      )}

      {metricsHistory && metricsHistory.length > 0 && (
        <Card>
          <SectionTitle>
            Usage Trend (Last {metricsHistory.length} days)
          </SectionTitle>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="pb-2 pr-3">Date</th>
                  <th className="pb-2 pr-3">Active</th>
                  <th className="pb-2 pr-3">Logins</th>
                  <th className="pb-2 pr-3">Invoices</th>
                  <th className="pb-2 pr-3">Sync</th>
                  <th className="pb-2 pr-3">Errors</th>
                </tr>
              </thead>
              <tbody>
                {metricsHistory.slice(-14).map((m) => (
                  <tr key={m.date} className="border-b border-slate-50">
                    <td className="py-1.5 pr-3 text-slate-600">
                      {new Date(m.date).toLocaleDateString()}
                    </td>
                    <td className="py-1.5 pr-3">{m.activeUsers}</td>
                    <td className="py-1.5 pr-3">{m.totalLogins}</td>
                    <td className="py-1.5 pr-3">{m.totalInvoices}</td>
                    <td className="py-1.5 pr-3">{m.syncEvents}</td>
                    <td className="py-1.5 pr-3">{m.errorRate.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Storage Tab ────────────────────────────────────────────

function StorageTab({ storageData }: { storageData?: StorageUsage }) {
  if (!storageData) {
    return (
      <Card>
        <SectionTitle>Storage</SectionTitle>
        <p className="mt-4 text-sm text-slate-400">
          No storage data available yet.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <SectionTitle>Storage Utilization</SectionTitle>
        <div className="mt-4 space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold text-slate-900">
                {storageData.utilizationPercent.toFixed(1)}%
              </p>
              <p className="text-sm text-slate-500">
                {formatMB(storageData.totalStorageMB)} of{" "}
                {formatMB(storageData.quotaLimitMB)} used
              </p>
            </div>
            {storageData.daysUntilFull && (
              <div className="text-right">
                <p className="text-lg font-semibold text-slate-900">
                  {storageData.daysUntilFull} days
                </p>
                <p className="text-xs text-slate-400">until full</p>
              </div>
            )}
          </div>
          <div className="h-4 w-full rounded-full bg-slate-100">
            <div
              className={`h-4 rounded-full ${
                storageData.utilizationPercent > 90
                  ? "bg-red-500"
                  : storageData.utilizationPercent > 70
                    ? "bg-yellow-500"
                    : "bg-green-500"
              }`}
              style={{
                width: `${Math.min(storageData.utilizationPercent, 100)}%`,
              }}
            />
          </div>
          {storageData.dailyGrowthMB !== null && (
            <p className="text-xs text-slate-400">
              Daily growth: {formatMB(storageData.dailyGrowthMB)}
            </p>
          )}
        </div>
      </Card>

      <Card>
        <SectionTitle>Data Breakdown</SectionTitle>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: "Products", value: storageData.productCount },
            { label: "Batches", value: storageData.batchCount },
            { label: "Invoices", value: storageData.invoiceCount },
            { label: "Customers", value: storageData.customerCount },
            { label: "Purchase Orders", value: storageData.purchaseOrderCount },
            { label: "Audit Logs", value: storageData.auditLogCount },
            { label: "Sync Events", value: storageData.syncEventCount },
            { label: "Users", value: storageData.userCount },
            { label: "Stores", value: storageData.storeCount },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3"
            >
              <span className="text-sm text-slate-600">{item.label}</span>
              <span className="text-sm font-semibold text-slate-900">
                {item.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <SectionTitle>Size Breakdown</SectionTitle>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-100 p-4 text-center">
            <p className="text-2xl font-semibold text-slate-900">
              {formatMB(storageData.estimatedDbSizeMB)}
            </p>
            <p className="text-xs text-slate-400">Database</p>
          </div>
          <div className="rounded-lg border border-slate-100 p-4 text-center">
            <p className="text-2xl font-semibold text-slate-900">
              {formatMB(storageData.fileStorageMB)}
            </p>
            <p className="text-xs text-slate-400">File Storage</p>
          </div>
          <div className="rounded-lg border border-slate-100 p-4 text-center">
            <p className="text-2xl font-semibold text-slate-900">
              {formatMB(storageData.totalStorageMB)}
            </p>
            <p className="text-xs text-slate-400">Total</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── Errors Tab ─────────────────────────────────────────────

function ErrorsTab({
  errorSummary,
  errorGroups,
}: {
  errorSummary?: ErrorSummary;
  errorGroups?: { data: ErrorGroup[]; total: number };
}) {
  return (
    <div className="space-y-6">
      {errorSummary && (
        <div className="grid gap-4 sm:grid-cols-4">
          <Card>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Open
            </p>
            <p className="mt-2 text-3xl font-semibold text-red-600">
              {errorSummary.open}
            </p>
          </Card>
          <Card>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Regressed
            </p>
            <p className="mt-2 text-3xl font-semibold text-orange-600">
              {errorSummary.regressed}
            </p>
          </Card>
          <Card>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Resolved
            </p>
            <p className="mt-2 text-3xl font-semibold text-green-600">
              {errorSummary.resolved}
            </p>
          </Card>
          <Card>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              New Today
            </p>
            <p className="mt-2 text-3xl font-semibold text-slate-900">
              {errorSummary.today}
            </p>
          </Card>
        </div>
      )}

      {errorSummary?.topTypes && errorSummary.topTypes.length > 0 && (
        <Card>
          <SectionTitle>Top Error Types</SectionTitle>
          <div className="mt-4 space-y-2">
            {errorSummary.topTypes.map((t) => (
              <div
                key={t.errorType}
                className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-2"
              >
                <span className="text-sm font-medium text-slate-700">
                  {t.errorType}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                  {t._count._all}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {errorGroups && errorGroups.data.length > 0 ? (
        <Card>
          <SectionTitle>
            Recent Error Groups ({errorGroups.total} total)
          </SectionTitle>
          <div className="mt-4 space-y-3">
            {errorGroups.data.map((group) => (
              <div
                key={group.id}
                className="rounded-xl border border-slate-100 p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {group.message}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          group.status === "open"
                            ? "bg-red-50 text-red-600"
                            : group.status === "regressed"
                              ? "bg-orange-50 text-orange-600"
                              : group.status === "resolved"
                                ? "bg-green-50 text-green-600"
                                : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {group.status}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                        {group.errorType}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                        {group.platform}
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-lg font-semibold text-slate-900">
                      {group.occurrenceCount}
                    </p>
                    <p className="text-xs text-slate-400">
                      {group.affectedUsers} users
                    </p>
                  </div>
                </div>
                {group.source && (
                  <p className="mt-2 text-xs text-slate-400">
                    Source: {group.source}
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-400">
                  First: {new Date(group.firstSeenAt).toLocaleDateString()} ·
                  Last: {new Date(group.lastSeenAt).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card>
          <SectionTitle>Error Groups</SectionTitle>
          <p className="mt-4 text-sm text-slate-400">
            No errors recorded for this tenant.
          </p>
        </Card>
      )}
    </div>
  );
}

// ── Presence Tab ───────────────────────────────────────────

function PresenceTab({ onlineUsers }: { onlineUsers?: OnlineConnection[] }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Online Now
          </p>
          <p className="mt-2 text-3xl font-semibold text-green-600">
            {onlineUsers?.length ?? 0}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Platforms
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {onlineUsers && onlineUsers.length > 0 ? (
              Object.entries(
                onlineUsers.reduce<Record<string, number>>((acc, u) => {
                  acc[u.platform] = (acc[u.platform] || 0) + 1;
                  return acc;
                }, {}),
              ).map(([platform, count]) => (
                <span
                  key={platform}
                  className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600"
                >
                  {platform}: {count}
                </span>
              ))
            ) : (
              <span className="text-sm text-slate-400">None</span>
            )}
          </div>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Pending Syncs
          </p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {onlineUsers?.reduce((sum, u) => sum + u.syncPendingChanges, 0) ??
              0}
          </p>
        </Card>
      </div>

      {onlineUsers && onlineUsers.length > 0 ? (
        <Card>
          <SectionTitle>Connected Devices</SectionTitle>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="pb-2 pr-4">User</th>
                  <th className="pb-2 pr-4">Device</th>
                  <th className="pb-2 pr-4">Platform</th>
                  <th className="pb-2 pr-4">Version</th>
                  <th className="pb-2 pr-4">Page</th>
                  <th className="pb-2 pr-4">Pending Sync</th>
                  <th className="pb-2 pr-4">Last Heartbeat</th>
                </tr>
              </thead>
              <tbody>
                {onlineUsers.map((conn) => (
                  <tr key={conn.deviceId} className="border-b border-slate-50">
                    <td className="py-2 pr-4 font-mono text-xs text-slate-700">
                      {conn.userId.slice(0, 8)}...
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-500">
                      {conn.deviceId.slice(0, 8)}...
                    </td>
                    <td className="py-2 pr-4">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                        {conn.platform}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-xs text-slate-500">
                      {conn.appVersion || "—"}
                    </td>
                    <td className="py-2 pr-4 text-xs text-slate-500">
                      {conn.appState?.currentPage || "—"}
                    </td>
                    <td className="py-2 pr-4">
                      {conn.syncPendingChanges > 0 ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-600">
                          {conn.syncPendingChanges}
                        </span>
                      ) : (
                        <span className="text-xs text-green-600">synced</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs text-slate-400">
                      {new Date(conn.lastHeartbeat).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <SectionTitle>Connected Devices</SectionTitle>
          <p className="mt-4 text-sm text-slate-400">
            No users are currently online.
          </p>
        </Card>
      )}
    </div>
  );
}

// ── Usage Tab ──────────────────────────────────────────────

function UsageTab({ tenantId }: { tenantId: string }) {
  const thirtyDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const { data: metrics, isLoading } = useQuery<DailyMetrics[]>({
    queryKey: ["tenant-metrics", tenantId],
    queryFn: () =>
      monitoringService.getTenantMetrics(tenantId, {
        startDate: thirtyDaysAgo,
      }),
  });

  if (isLoading)
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        Loading usage data…
      </p>
    );

  const totalRevenue = metrics?.reduce((s, m) => s + m.invoiceAmount, 0) ?? 0;
  const totalInvoices = metrics?.reduce((s, m) => s + m.totalInvoices, 0) ?? 0;
  const totalLogins = metrics?.reduce((s, m) => s + m.totalLogins, 0) ?? 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Revenue (30d)
          </p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            ₹{totalRevenue.toLocaleString()}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Invoices (30d)
          </p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {totalInvoices.toLocaleString()}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Logins (30d)
          </p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {totalLogins.toLocaleString()}
          </p>
        </Card>
      </div>

      <Card>
        <h3 className="mb-4 text-sm font-semibold text-slate-700">
          Daily Breakdown
        </h3>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="pb-2">Date</th>
                <th className="pb-2 text-right">Invoices</th>
                <th className="pb-2 text-right">Revenue</th>
                <th className="pb-2 text-right">Logins</th>
                <th className="pb-2 text-right">Active Users</th>
              </tr>
            </thead>
            <tbody>
              {metrics && metrics.length > 0 ? (
                metrics.map((m) => (
                  <tr key={m.date} className="border-b border-slate-100">
                    <td className="py-2">
                      {new Date(m.date).toLocaleDateString()}
                    </td>
                    <td className="py-2 text-right">{m.totalInvoices}</td>
                    <td className="py-2 text-right">
                      ₹{m.invoiceAmount.toLocaleString()}
                    </td>
                    <td className="py-2 text-right">{m.totalLogins}</td>
                    <td className="py-2 text-right">{m.activeUsers}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-slate-400">
                    No usage data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Sessions Tab ───────────────────────────────────────────

function SessionsTab({ tenantId }: { tenantId: string }) {
  const { data: connections, isLoading } = useQuery<OnlineConnection[]>({
    queryKey: ["tenant-sessions", tenantId],
    queryFn: () => monitoringService.getOnlineConnections({ tenantId }),
  });

  if (isLoading)
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        Loading sessions…
      </p>
    );

  const activeSessions = connections ?? [];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Active Sessions
          </p>
          <p className="mt-2 text-3xl font-semibold text-green-600">
            {activeSessions.length}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Platforms
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {activeSessions.length > 0 ? (
              Object.entries(
                activeSessions.reduce<Record<string, number>>((acc, s) => {
                  acc[s.platform] = (acc[s.platform] || 0) + 1;
                  return acc;
                }, {}),
              ).map(([platform, count]) => (
                <span
                  key={platform}
                  className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600"
                >
                  {platform}: {count}
                </span>
              ))
            ) : (
              <span className="text-sm text-slate-400">None</span>
            )}
          </div>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Pending Syncs
          </p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {activeSessions.reduce((sum, s) => sum + s.syncPendingChanges, 0)}
          </p>
        </Card>
      </div>

      <Card>
        <h3 className="mb-4 text-sm font-semibold text-slate-700">
          Active Sessions
        </h3>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="pb-2">User</th>
                <th className="pb-2">Platform</th>
                <th className="pb-2">Version</th>
                <th className="pb-2">Connected</th>
                <th className="pb-2 text-right">Pending</th>
              </tr>
            </thead>
            <tbody>
              {activeSessions.length > 0 ? (
                activeSessions.map((s) => (
                  <tr key={s.userId} className="border-b border-slate-100">
                    <td className="py-2 font-medium text-slate-900">
                      {s.userId}
                    </td>
                    <td className="py-2">{s.platform}</td>
                    <td className="py-2">{s.appVersion ?? "—"}</td>
                    <td className="py-2">
                      {new Date(s.lastHeartbeat).toLocaleString()}
                    </td>
                    <td className="py-2 text-right">{s.syncPendingChanges}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-slate-400">
                    No active sessions
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Alerts Tab ─────────────────────────────────────────────

function AlertsTab({ tenantId }: { tenantId: string }) {
  const { data: alerts, isLoading } = useQuery({
    queryKey: ["tenant-alerts", tenantId],
    queryFn: () => monitoringService.getActiveAlerts(),
    select: (data: any[]) => data.filter((a: any) => a.tenantId === tenantId),
  });

  if (isLoading)
    return (
      <p className="py-8 text-center text-sm text-slate-400">Loading alerts…</p>
    );

  const alertList = alerts ?? [];
  const openCount = alertList.filter((a: any) => a.status === "open").length;
  const ackCount = alertList.filter(
    (a: any) => a.status === "acknowledged",
  ).length;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Active Alerts
          </p>
          <p className="mt-2 text-3xl font-semibold text-red-600">
            {alertList.length}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Open
          </p>
          <p className="mt-2 text-3xl font-semibold text-orange-600">
            {openCount}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Acknowledged
          </p>
          <p className="mt-2 text-3xl font-semibold text-blue-600">
            {ackCount}
          </p>
        </Card>
      </div>

      <Card>
        <h3 className="mb-4 text-sm font-semibold text-slate-700">
          Alert History
        </h3>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="pb-2">Severity</th>
                <th className="pb-2">Rule</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Triggered</th>
              </tr>
            </thead>
            <tbody>
              {alertList.length > 0 ? (
                alertList.map((a: any) => (
                  <tr key={a.id} className="border-b border-slate-100">
                    <td className="py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          a.severity === "critical"
                            ? "bg-red-100 text-red-700"
                            : a.severity === "warning"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {a.severity}
                      </span>
                    </td>
                    <td className="py-2 text-slate-900">
                      {a.rule?.name ?? "—"}
                    </td>
                    <td className="py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          a.status === "open"
                            ? "bg-red-100 text-red-700"
                            : a.status === "acknowledged"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-green-100 text-green-700"
                        }`}
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="py-2">
                      {new Date(a.triggeredAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-slate-400">
                    No alerts for this tenant
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Lifecycle Tab ──────────────────────────────────────────

function LifecycleTab({ tenantId }: { tenantId: string }) {
  const { data: lifecycle, isLoading } = useQuery({
    queryKey: ["tenant-lifecycle", tenantId],
    queryFn: () => monitoringService.getTenantLifecycle(tenantId),
  });

  const { data: readiness, isLoading: isLoadingReadiness } = useQuery({
    queryKey: ["tenant-readiness", tenantId],
    queryFn: () => tenantService.getReadiness(tenantId),
    enabled: Boolean(tenantId),
  });

  if (isLoading)
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        Loading lifecycle…
      </p>
    );

  const lc = lifecycle as any;

  const stageColor = (stage: string) => {
    switch (stage?.toLowerCase()) {
      case "onboarding":
        return "bg-blue-100 text-blue-700";
      case "active":
        return "bg-green-100 text-green-700";
      case "growing":
        return "bg-emerald-100 text-emerald-700";
      case "at_risk":
        return "bg-yellow-100 text-yellow-700";
      case "churning":
        return "bg-orange-100 text-orange-700";
      case "churned":
        return "bg-red-100 text-red-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">
              Server-owned store readiness
            </h3>
            <p className="mt-1 text-xs text-slate-400">
              Store {readiness?.storeId ?? "—"} · recomputed from operational
              evidence
            </p>
          </div>
          <span className="text-2xl font-semibold text-slate-900">
            {isLoadingReadiness ? "…" : `${readiness?.percentComplete ?? 0}%`}
          </span>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-green-500"
            style={{ width: `${readiness?.percentComplete ?? 0}%` }}
          />
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {readiness?.checklist.map((step) => (
            <div
              key={step.id}
              className="rounded-xl border border-slate-100 p-3"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">
                  {step.title}
                </p>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                    step.completed
                      ? "bg-green-50 text-green-700"
                      : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {step.completed ? "Verified" : "Pending"}
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {step.evidence.map((evidence) => evidence.detail).join(" ")}
              </p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Current Stage
          </p>
          <p className="mt-2">
            <span
              className={`rounded-full px-3 py-1 text-sm font-semibold ${stageColor(lc?.stage)}`}
            >
              {lc?.stage ?? "Unknown"}
            </span>
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Health Score
          </p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {lc?.healthScore ?? "—"}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Days Since Signup
          </p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">
            {lc?.createdAt
              ? Math.floor(
                  (Date.now() - new Date(lc.createdAt).getTime()) / 86400000,
                )
              : "—"}
          </p>
        </Card>
      </div>

      <Card>
        <h3 className="mb-4 text-sm font-semibold text-slate-700">
          Risk Indicators
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs text-slate-400">Churn Risk</p>
            <p className="text-lg font-semibold text-slate-900">
              {lc?.churnRisk ?? "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Last Active</p>
            <p className="text-lg font-semibold text-slate-900">
              {lc?.lastActiveAt
                ? new Date(lc.lastActiveAt).toLocaleDateString()
                : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400">MRR</p>
            <p className="text-lg font-semibold text-slate-900">
              {lc?.mrr != null ? `₹${Number(lc.mrr).toLocaleString()}` : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Engagement Score</p>
            <p className="text-lg font-semibold text-slate-900">
              {lc?.engagementScore ?? "—"}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── Configuration Tab ──────────────────────────────────────

function ConfigTab({
  data,
  statusValue,
  setStatus,
  partnerCode,
  setPartnerCode,
  tenantChangeReason,
  setTenantChangeReason,
  isPending,
  mutateAsync,
  tenantId,
  isForceLoggingOut,
  forceLogout,
  isSyncingPermissions,
  syncPermissions,
  plans,
  selectedPlanId,
  setSelectedPlanId,
  billingInterval,
  setBillingInterval,
  useTrial,
  setUseTrial,
  selectedPlan,
  subscriptionAssignmentReason,
  setSubscriptionAssignmentReason,
  isCreatingSubscription,
  createSubscription,
  selectedUserId,
  setSelectedUserId,
  impersonationReason,
  setImpersonationReason,
  capabilityReason,
  setCapabilityReason,
  isStartingImpersonation,
  startImpersonation,
  impersonationLaunchUrl,
  impersonationLogId,
  isStoppingImpersonation,
  stopImpersonation,
  allModules,
  tenantModules,
  isLoadingModules,
  isTogglingModule,
  toggleModule,
  pagePolicies,
  tenantPageOverrides,
  isLoadingPagePolicies,
  isLoadingPageOverrides,
  isPagePoliciesError,
  isPageOverridesError,
  pageOverrideReason,
  setPageOverrideReason,
  isSettingPageMode,
  setPageMode,
  editingStoreId,
  setEditingStoreId,
  storeDraft,
  setStoreDraft,
  isUpdatingStore,
  updateStore,
  isUpdatingPaymentMethod,
  updatePaymentMethod,
  isUpdatingBillingPrinter,
  updateBillingPrinter,
}: any) {
  const navigationPagePolicies = Array.isArray(pagePolicies)
    ? pagePolicies.filter((policy: any) => policy?.navigation)
    : [];

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <RoleGate
          role="SUPPORT_ADMIN"
          fallback={
            <Card className="bg-slate-50">
              <SectionTitle>Admin Controls</SectionTitle>
              <p className="mt-4 text-sm text-slate-500">
                Support Admin access is required to change tenant lifecycle or
                configuration.
              </p>
            </Card>
          }
        >
          <Card>
          <SectionTitle>Admin Controls</SectionTitle>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Status
              </label>
              <div className="mt-2">
                <StatusSelect value={statusValue} onChange={setStatus} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Partner code
              </label>
              <input
                value={partnerCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPartnerCode(e.target.value)
                }
                className="mt-2 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                placeholder="PARTNER-01"
              />
            </div>
            {statusValue !== data.status && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Lifecycle change reason
                </label>
                <input
                  value={tenantChangeReason}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setTenantChangeReason(event.target.value)
                  }
                  className="mt-2 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  placeholder="Required reason (8+ characters)"
                />
              </div>
            )}
            <button
              type="button"
              disabled={
                isPending ||
                (statusValue !== data.status &&
                  tenantChangeReason.trim().length < 8)
              }
              onClick={() =>
                mutateAsync({
                  id: tenantId,
                  payload: {
                    status:
                      statusValue !== data.status ? statusValue : undefined,
                    partnerCode: partnerCode || undefined,
                    reason:
                      statusValue !== data.status
                        ? tenantChangeReason.trim()
                        : undefined,
                  },
                })
              }
              className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isPending ? "Saving..." : "Save changes"}
            </button>
            <button
              type="button"
              disabled={isForceLoggingOut}
              onClick={() => {
                if (
                  window.confirm(
                    "Force logout all users of this tenant? All active sessions will be terminated immediately.",
                  )
                ) {
                  forceLogout();
                }
              }}
              className="rounded-xl bg-red-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isForceLoggingOut ? "Logging out..." : "Force logout all users"}
            </button>
            <RoleGate role="SUPER_ADMIN">
              <button
                type="button"
                disabled={isSyncingPermissions}
                onClick={() => {
                  if (
                    window.confirm(
                      "Sync base role permissions for this tenant? This will add any missing permissions to BUSINESS_OWNER, STORE_MANAGER, PHARMACIST, and CASHIER roles without removing existing ones. All users will be forced to re-authenticate.",
                    )
                  ) {
                    syncPermissions();
                  }
                }}
                className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSyncingPermissions
                  ? "Syncing..."
                  : "Sync base role permissions"}
              </button>
            </RoleGate>
          </div>
          </Card>
        </RoleGate>

        <RoleGate
          role="SUPER_ADMIN"
          fallback={
            <Card className="bg-slate-50">
              <SectionTitle>Subscriptions</SectionTitle>
              <p className="mt-4 text-sm text-slate-500">
                Subscription assignment requires Super Admin access.
              </p>
            </Card>
          }
        >
          <Card>
          <SectionTitle>Subscriptions</SectionTitle>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <div className="rounded-xl border border-slate-100 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Add subscription
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <select
                  value={selectedPlanId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSelectedPlanId(e.target.value)
                  }
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                >
                  {plans?.map((plan: any) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.type})
                    </option>
                  ))}
                </select>
                <select
                  value={billingInterval}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setBillingInterval(e.target.value)
                  }
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  <input
                    type="checkbox"
                    checked={useTrial}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUseTrial(e.target.checked)
                    }
                    disabled={!selectedPlan?.trialDays}
                  />
                  Trial
                </label>
              </div>
              <input
                value={subscriptionAssignmentReason}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSubscriptionAssignmentReason(event.target.value)
                }
                placeholder="Required assignment reason (8+ characters)"
                className="mt-3 h-9 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
              <button
                type="button"
                disabled={
                  !selectedPlanId ||
                  isCreatingSubscription ||
                  subscriptionAssignmentReason.trim().length < 8
                }
                onClick={() => createSubscription()}
                className="mt-3 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white"
              >
                {isCreatingSubscription ? "Creating..." : "Assign plan"}
              </button>
            </div>
            {data.subscriptions.length ? (
              data.subscriptions.map((subscription: any) => (
                <div
                  key={subscription.id}
                  className="rounded-xl border border-slate-100 p-4"
                >
                  <p className="font-semibold text-slate-900">
                    {subscription.plan.name} · {subscription.status}
                  </p>
                  <p>
                    {subscription.interval} · &#8377;{subscription.amount}
                  </p>
                </div>
              ))
            ) : (
              <p>No active subscriptions.</p>
            )}
          </div>
          </Card>
        </RoleGate>
      </div>

      <RoleGate
        anyRole={["SUPER_ADMIN", "SUPPORT_ADMIN"]}
        fallback={
          <Card className="bg-slate-50">
            <SectionTitle>Impersonation</SectionTitle>
            <p className="mt-4 text-sm text-slate-500">
              You don&apos;t have permission to impersonate users. Contact a
              Super Admin.
            </p>
          </Card>
        }
      >
        <Card>
          <SectionTitle>Impersonation</SectionTitle>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <select
              value={selectedUserId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedUserId(e.target.value)
              }
              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm"
            >
              {data.users.map((user: any) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName ?? ""} ({user.email})
                </option>
              ))}
            </select>
            <input
              value={impersonationReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setImpersonationReason(e.target.value)
              }
              placeholder="Required support reason"
              className="h-10 rounded-xl border border-slate-200 px-4 text-sm"
            />
            <button
              type="button"
              disabled={
                !selectedUserId ||
                impersonationReason.trim().length < 8 ||
                isStartingImpersonation
              }
              onClick={() => startImpersonation()}
              className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
            >
              {isStartingImpersonation ? "Starting..." : "Start impersonation"}
            </button>
          </div>
          {impersonationLaunchUrl && (
            <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Scoped support session
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Opens a single-use, 10-minute tenant session. No refresh token
                is issued and stopping the session revokes it immediately.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={impersonationLaunchUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600"
                >
                  Open tenant app
                </a>
                <button
                  type="button"
                  disabled={!impersonationLogId || isStoppingImpersonation}
                  onClick={() => stopImpersonation()}
                  className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  {isStoppingImpersonation ? "Stopping..." : "Stop session"}
                </button>
              </div>
            </div>
          )}
        </Card>
      </RoleGate>

      <RoleGate
        anyRole={["SUPER_ADMIN", "SUPPORT_ADMIN"]}
        fallback={
          <Card className="bg-slate-50">
            <SectionTitle>Capability Overrides</SectionTitle>
            <p className="mt-2 text-sm text-slate-500">
              Elevated support access is required to change tenant capabilities.
            </p>
          </Card>
        }
      >
        <div className="space-y-6">
          <Card>
            <div>
              <SectionTitle>Capability Overrides</SectionTitle>
              <p className="mt-1 text-xs text-slate-400">
                A reason is required and the operator is recorded for every
                change.
              </p>
              <input
                value={capabilityReason}
                onChange={(event) => setCapabilityReason(event.target.value)}
                placeholder="Required override reason (8+ characters)"
                className="mt-4 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
              />
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {isLoadingModules ? (
                <p className="col-span-full text-sm text-slate-500">
                  Loading modules...
                </p>
              ) : allModules?.length ? (
                allModules.map((mod: any) => {
                  const isEnabled =
                    tenantModules?.enabled?.includes(mod.code) ?? false;
                  const detail = tenantModules?.all?.find(
                    (m: any) => m.code === mod.code,
                  );
                  const source = detail?.source;
                  const expiresAt = detail?.expiresAt;
                  const mode =
                    source === "plan" || source === "PLAN"
                      ? "DEFAULT"
                      : isEnabled
                        ? "ENABLED"
                        : "DISABLED";
                  return (
                    <div
                      key={mod.code}
                      className="flex items-start gap-3 rounded-xl border border-slate-100 px-4 py-3 text-sm transition hover:bg-slate-50"
                    >
                      <select
                        value={mode}
                        disabled={
                          isTogglingModule || capabilityReason.trim().length < 8
                        }
                        onChange={(event) =>
                          toggleModule({
                            code: mod.code,
                            mode: event.target.value,
                          })
                        }
                        className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 disabled:opacity-60"
                      >
                        <option value="DEFAULT">Default</option>
                        <option value="ENABLED">Enabled</option>
                        <option value="DISABLED">Disabled</option>
                      </select>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900">
                          {mod.name}
                        </p>
                        <p className="text-xs text-slate-400">{mod.code}</p>
                        {isEnabled && source && (
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <span
                              className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                source === "plan"
                                  ? "bg-blue-50 text-blue-600"
                                  : source === "PLAN"
                                    ? "bg-blue-50 text-blue-600"
                                    : source === "ADDON"
                                      ? "bg-amber-50 text-amber-600"
                                      : "bg-purple-50 text-purple-600"
                              }`}
                            >
                              {source === "plan"
                                ? "Plan"
                                : source === "PLAN"
                                  ? "Plan"
                                  : source === "ADDON"
                                    ? "Add-on"
                                    : source === "TRIAL"
                                      ? "Trial"
                                      : "Override"}
                            </span>
                            {expiresAt && (
                              <span className="text-[10px] text-orange-500">
                                Expires{" "}
                                {new Date(expiresAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="col-span-full text-sm text-slate-500">
                  No modules configured.
                </p>
              )}
            </div>
          </Card>

          <Card>
            <SectionTitle>Page & Sidebar Controls</SectionTitle>
            <p className="mt-1 text-xs text-slate-400">
              Default follows module and permission access. Hidden removes the
              sidebar item but keeps direct URL access. Denied removes the item
              and blocks direct URL/API policy access.
            </p>
            <input
              value={pageOverrideReason}
              onChange={(event) => setPageOverrideReason(event.target.value)}
              placeholder="Required page-control reason (8+ characters)"
              className="mt-4 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
            />
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(9rem,auto)] gap-3 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                <span>Customer page</span>
                <span>Tenant mode</span>
              </div>
              {isLoadingPagePolicies || isLoadingPageOverrides ? (
                <p className="px-4 py-6 text-sm text-slate-500">
                  Loading page policies...
                </p>
              ) : isPagePoliciesError || isPageOverridesError ? (
                <p className="px-4 py-6 text-sm text-red-600">
                  Page controls could not be loaded. Restart the API and try
                  again.
                </p>
              ) : navigationPagePolicies.length ? (
                navigationPagePolicies.map((policy: any) => {
                  const override = tenantPageOverrides?.overrides?.find(
                    (item: any) =>
                      item.pageKey === policy.key && item.effective,
                  );
                  const requiredModuleEnabled =
                    !policy.requiredModule ||
                    tenantModules?.enabled?.includes(policy.requiredModule);
                  return (
                    <div
                      key={policy.key}
                      className="grid grid-cols-[minmax(0,1fr)_minmax(9rem,auto)] items-center gap-3 border-t border-slate-100 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">
                            {policy.label}
                          </p>
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                            {policy.section}
                          </span>
                          {!requiredModuleEnabled && (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                              Module off
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-slate-400">
                          {policy.path}
                          {policy.requiredModule
                            ? ` · ${policy.requiredModule}`
                            : ""}
                          {policy.requiredPermission
                            ? ` · ${policy.requiredPermission}`
                            : ""}
                        </p>
                        {override?.reason && (
                          <p className="mt-1 truncate text-[11px] text-slate-500">
                            Reason: {override.reason}
                          </p>
                        )}
                      </div>
                      <select
                        value={override?.mode ?? "DEFAULT"}
                        disabled={isSettingPageMode}
                        onChange={(event) => {
                          if (pageOverrideReason.trim().length < 8) {
                            toast.error(
                              "Enter a reason of at least 8 characters",
                            );
                            return;
                          }
                          setPageMode({
                            pageKey: policy.key,
                            mode: event.target.value,
                          });
                        }}
                        className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 disabled:opacity-60"
                      >
                        <option value="DEFAULT">Default</option>
                        <option value="HIDDEN">Hidden</option>
                        <option value="DENIED">Denied</option>
                      </select>
                    </div>
                  );
                })
              ) : (
                <p className="px-4 py-6 text-sm text-slate-500">
                  No navigation page policies are configured.
                </p>
              )}
            </div>
            <p className="mt-3 text-xs text-slate-400">
              Access version {tenantPageOverrides?.accessVersion ?? "—"}.
              Connected users receive the new policy on their next authenticated
              request.
            </p>
          </Card>
        </div>
      </RoleGate>

      <RoleGate
        role="SUPPORT_ADMIN"
        fallback={
          <Card className="bg-slate-50">
            <SectionTitle>Stores</SectionTitle>
            <p className="mt-4 text-sm text-slate-500">
              Store, printer and payment-method changes require Support Admin
              access.
            </p>
          </Card>
        }
      >
        <Card>
          <SectionTitle>Stores</SectionTitle>
        <div className="mt-4 space-y-3">
          {data.stores.length ? (
            data.stores.map((store: any) => (
              <div
                key={store.id}
                className="flex flex-col gap-3 rounded-xl border border-slate-100 px-4 py-3 text-sm text-slate-600"
              >
                {editingStoreId === store.id ? (
                  <div className="grid gap-3 md:grid-cols-4">
                    <input
                      value={storeDraft.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setStoreDraft((prev: any) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
                      placeholder="Store name"
                    />
                    <input
                      value={storeDraft.city}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setStoreDraft((prev: any) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
                      placeholder="City"
                    />
                    <input
                      value={storeDraft.state}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setStoreDraft((prev: any) => ({
                          ...prev,
                          state: e.target.value,
                        }))
                      }
                      className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
                      placeholder="State"
                    />
                    <StatusSelect
                      value={storeDraft.status}
                      onChange={(value: string) =>
                        setStoreDraft((prev: any) => ({
                          ...prev,
                          status: value,
                        }))
                      }
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {store.name}
                        </p>
                        <p>
                          {store.city}, {store.state}
                        </p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs">
                        {store.status}
                      </span>
                    </div>
                    <div className="grid gap-3 rounded-xl bg-slate-50 p-3 md:grid-cols-2">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Billing printer
                        </p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold text-slate-800">
                              {store.billingPrinter?.name ?? "Not created"}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {store.billingPrinter?.printerType ?? "—"} ·{" "}
                              {store.billingPrinter?.isActive
                                ? "Active"
                                : "Setup required"}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={isUpdatingBillingPrinter}
                            onClick={() =>
                              updateBillingPrinter({
                                storeId: store.id,
                                active: !store.billingPrinter?.isActive,
                              })
                            }
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase"
                          >
                            {store.billingPrinter?.isActive
                              ? "Disable"
                              : "Enable"}
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Payment methods
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(store.paymentMethods ?? []).map((payment: any) => (
                            <button
                              key={payment.id}
                              type="button"
                              disabled={isUpdatingPaymentMethod}
                              onClick={() =>
                                updatePaymentMethod({
                                  storeId: store.id,
                                  method: payment.method,
                                  enabled: !payment.isEnabled,
                                })
                              }
                              className={`rounded-full px-2 py-1 text-[10px] font-bold ${
                                payment.isEnabled
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {payment.method} ·{" "}
                              {payment.isEnabled ? "Enabled" : "Disabled"} ·{" "}
                              {payment.configurationStatus === "READY"
                                ? "Ready"
                                : "Setup"}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2">
                  {editingStoreId === store.id ? (
                    <>
                      <button
                        type="button"
                        disabled={isUpdatingStore}
                        onClick={() =>
                          updateStore({
                            storeId: store.id,
                            payload: storeDraft,
                          })
                        }
                        className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingStoreId(null)}
                        className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingStoreId(store.id);
                        setStoreDraft({
                          name: store.name,
                          city: store.city,
                          state: store.state,
                          status: store.status,
                        });
                      }}
                      className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">No stores listed.</p>
          )}
        </div>
        </Card>
      </RoleGate>
    </div>
  );
}

export default TenantDetailPage;
